import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { SessionKeeper } from './auth/keeper.js';
import { createAuthProvider, type AuthProvider } from './auth/providers.js';
import { setUnauthorizedHandler } from './lib/api.js';
import { routeTree } from './generated/routeTree.gen.js';
import { RouteError, RoutePending } from './shell/route-states.js';

export interface RouterContext {
  queryClient: QueryClient;
  auth: AuthProvider;
}

export function createAppRouter() {
  const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
  });
  const auth = createAuthProvider();
  const router = createRouter({
    routeTree,
    context: { queryClient, auth },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: RoutePending,
    defaultErrorComponent: RouteError,
    scrollRestoration: true,
  });
  // Renews the session before it expires; when it can't, signs out with a reason (B-001).
  const keeper = new SessionKeeper({
    auth,
    onSessionEnded: () => {
      queryClient.clear();
      const here = router.state.location;
      if (here.pathname === '/login') return;
      void router.navigate({ to: '/login', search: { redirect: here.href, reason: 'expired' } });
    },
  });
  setUnauthorizedHandler((refused) => keeper.recover(refused));
  return { router, queryClient, keeper };
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>['router'];
  }
}
